/**
 * TweakablePanel — shows tweakable params nested by definition/instance hierarchy.
 *
 * Used as both a standalone view (tweak mode) and an overlay on the 3D canvas.
 * Scoped to a definition via `scopeDefinitionId` (defaults to 'root').
 *
 * ## Tree structure (example: root contains ball1, ball2 instances of "ball")
 *
 *   ┌ BALL (DefinitionGroup) ──────────────────────┐
 *   │ metalness   ← shared: tweaks ball's config   │  directParams from
 *   │ opacity     ← shared: tweaks ball's config   │  buildTweakableTree('ball')
 *   │ ┌ BALL1 (InstanceEntry) ───────────────────┐ │
 *   │ │ roughness ← per-instance: tweaks root's  │ │  instance.endpoints
 *   │ │             config for ball1              │ │
 *   │ └─────────────────────────────────────────┘ │
 *   │ ┌ BALL2 (InstanceEntry) ───────────────────┐ │
 *   │ │ roughness ← per-instance: tweaks root's  │ │
 *   │ │             config for ball2              │ │
 *   │ └─────────────────────────────────────────┘ │
 *   └─────────────────────────────────────────────┘
 *
 * Only shows params marked as `tweakable`. Exposed API params can be tweaked
 * from the graph editor's node inspector instead.
 * All tweaks use definitionId mode (TweakControl with definitionId prop).
 */

import { useMemo } from 'react';
import { useRuntime } from '../../contexts/RuntimeProvider';
import { TweakControl } from './TweakControl';
import type { EndpointState } from '@/nodes/Endpoint';
import type { DefinitionStateMap } from '@/runtime/RuntimeStore';

// ── Tree types ──

/** A tweakable param on a primitive node. `definitionId` is the definition containing the node. */
interface DirectParam {
  /** Which definition to tweak (e.g. 'ball' for params inside ball, 'root' for root-level) */
  definitionId: string;
  nodeId: string;
  endpoint: EndpointState;
}

/** A sub-graph instance node (e.g. ball1 in root) with its tweakable params. */
interface InstanceEntry {
  nodeId: string;
  label?: string;
  /** The definition this instance lives in — tweaks go to this definition's config */
  parentDefinitionId: string;
  /** Tweakable endpoints on the instance node itself (exposed API params) */
  endpoints: EndpointState[];
}

/** All instances of one definition type, grouped together with the definition's shared params. */
interface DefinitionGroup {
  /** The definition type (e.g. 'ball') */
  definitionType: string;
  /** Shared params from inside the definition (recursive) */
  internalTree: TweakableTree;
  /** Each instance of this definition in the parent, with per-instance params */
  instances: InstanceEntry[];
}

interface TweakableTree {
  /** Tweakable params on primitive nodes at this level */
  directParams: DirectParam[];
  /** Sub-definitions containing tweakable params (recursive) */
  definitionGroups: DefinitionGroup[];
}

// ── Tree builder ──

/**
 * Recursively walk a definition's nodes, splitting them into:
 * - directParams: tweakable params on primitive nodes (rendered as sliders)
 * - definitionGroups: sub-graph instances grouped by type (rendered as nested boxes)
 *
 * Only includes branches that contain at least one tweakable param.
 * Same traversal pattern as InstanceTree.tsx's buildInstanceTree.
 */
function buildTweakableTree(
  definitionId: string,
  definitions: DefinitionStateMap
): TweakableTree {
  const def = definitions[definitionId];
  if (!def) return { directParams: [], definitionGroups: [] };

  const directParams: DirectParam[] = [];
  const instancesByType = new Map<string, InstanceEntry[]>();

  for (const [nodeId, node] of Object.entries(def.nodes)) {
    const tweakableEps = Object.values(node.endpoints).filter(
      ep => ep.dataFlow === 'param' && ep.tweakable
    );

    // Sub-graph instance? Group by definition type and recurse.
    const isSubInstance = definitions[node.type] && node.type !== 'root';
    if (isSubInstance) {
      if (!instancesByType.has(node.type)) instancesByType.set(node.type, []);
      instancesByType.get(node.type)!.push({
        nodeId,
        label: node.label,
        parentDefinitionId: definitionId,
        endpoints: tweakableEps,
      });
    } else {
      // Primitive node — collect tweakable params directly
      for (const ep of tweakableEps) {
        directParams.push({ definitionId, nodeId, endpoint: ep });
      }
    }
  }

  // For each sub-definition type, recurse for shared params and prune empty branches
  const definitionGroups: DefinitionGroup[] = [];
  for (const [defType, instances] of instancesByType) {
    const internalTree = buildTweakableTree(defType, definitions);
    const hasInstanceParams = instances.some(i => i.endpoints.length > 0);
    const hasInternalContent =
      internalTree.directParams.length > 0 ||
      internalTree.definitionGroups.length > 0;
    if (hasInstanceParams || hasInternalContent) {
      definitionGroups.push({ definitionType: defType, internalTree, instances });
    }
  }

  return { directParams, definitionGroups };
}

function treeIsEmpty(tree: TweakableTree): boolean {
  return tree.directParams.length === 0 && tree.definitionGroups.length === 0;
}

// ── Components ──

interface TweakablePanelProps {
  overlay?: boolean;
  scopeDefinitionId?: string;
}

export function TweakablePanel({ overlay, scopeDefinitionId = 'root' }: TweakablePanelProps = {}) {
  const { definitionState } = useRuntime();

  const tree = useMemo(
    () => buildTweakableTree(scopeDefinitionId, definitionState),
    [definitionState, scopeDefinitionId]
  );

  if (treeIsEmpty(tree)) {
    if (overlay) return null;
    return (
      <div style={containerStyle}>
        <div style={emptyStyle}>
          No tweakable params. Click the star on any param to mark it as tweakable.
        </div>
      </div>
    );
  }

  return (
    <div style={overlay ? overlayContainerStyle : containerStyle}>
      <div style={overlay ? overlayGridStyle : gridStyle}>
        <TreeView tree={tree} />
      </div>
    </div>
  );
}

/** Renders direct params + definition group boxes */
function TreeView({ tree }: { tree: TweakableTree }) {
  return (
    <>
      {tree.directParams.map(({ definitionId, nodeId, endpoint }) => (
        <div key={`${nodeId}.${endpoint.id}`} style={rowStyle}>
          <span style={labelStyle}>{endpoint.label || endpoint.id}</span>
          <TweakControl definitionId={definitionId} nodeId={nodeId} endpoint={endpoint} />
        </div>
      ))}

      {tree.definitionGroups.map(group => (
        <DefinitionGroupView key={group.definitionType} group={group} />
      ))}
    </>
  );
}

/** Outer definition box: shared params + instance boxes inside */
function DefinitionGroupView({ group }: { group: DefinitionGroup }) {
  const instancesWithParams = group.instances.filter(i => i.endpoints.length > 0);

  return (
    <div style={defBoxStyle}>
      <div style={defLabelStyle}>{group.definitionType}</div>

      {/* Shared definition-level params */}
      <TreeView tree={group.internalTree} />

      {/* Per-instance boxes */}
      {instancesWithParams.map(instance => (
        <div key={instance.nodeId} style={instanceBoxStyle}>
          <div style={instanceLabelStyle}>{instance.label || instance.nodeId}</div>
          {instance.endpoints.map(ep => (
            <div key={`${instance.nodeId}.${ep.id}`} style={rowStyle}>
              <span style={labelStyle}>{ep.label || ep.id}</span>
              <TweakControl
                definitionId={instance.parentDefinitionId}
                nodeId={instance.nodeId}
                endpoint={ep}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Styles ──

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  background: '#111827',
  overflowY: 'auto',
  padding: 24,
};

const overlayContainerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  left: 12,
  width: 360,
  maxHeight: 'calc(100% - 24px)',
  overflowY: 'auto',
  background: 'rgba(15, 23, 42, 0.85)',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.1)',
  backdropFilter: 'blur(8px)',
  padding: 12,
  pointerEvents: 'auto',
  zIndex: 10,
};

const emptyStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: 13,
  textAlign: 'center',
  marginTop: 80,
};

const gridStyle: React.CSSProperties = {
  maxWidth: 400,
  margin: '0 auto',
};

const overlayGridStyle: React.CSSProperties = {};

const defBoxStyle: React.CSSProperties = {
  border: '1px solid #374151',
  borderRadius: 6,
  padding: '8px 10px 6px',
  marginTop: 10,
};

const defLabelStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 4,
};

const instanceBoxStyle: React.CSSProperties = {
  border: '1px solid #1f2937',
  borderRadius: 5,
  padding: '6px 8px 4px',
  marginTop: 6,
};

const instanceLabelStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 3,
};

const rowStyle: React.CSSProperties = {
  padding: '4px 0',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'rgba(255,255,255,0.6)',
  display: 'block',
  marginBottom: 2,
};
